/**
 * TrazabilidadTab — recorrido completo de una reunión, del fin de la llamada al envío de la
 * notificación. Solo visible para AdminGlobal (ver accessMatrix). Arranca sin resultados:
 * no consulta el backend hasta que el administrador busca. El tenant del llamador lo resuelve
 * el backend a partir del token; el frontend nunca lo envía por query (ver `TraceQueryParams`).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePipelineTrace } from '@/hooks/usePipelineTrace';
import type {
  PipelineTrace, TraceMeetingMatch, TraceNotFoundReason, TraceQueryParams,
} from '@/api/types';
import { TraceFilters, type TraceFilterMode } from './TraceFilters';
import { TraceStagesTable } from './TraceStagesTable';
import { TraceRawLogs } from './TraceRawLogs';
import './TrazabilidadTab.css';

const SECTION_TITLE_CLASS = 'trc-section-title';

const NOT_FOUND_KEYS: Record<TraceNotFoundReason, string> = {
  EmailNotRegistered: 'trace.notFoundEmailNotRegistered',
  UserWithoutMeetings: 'trace.notFoundUserWithoutMeetings',
  SubjectNoMatches: 'trace.notFoundSubjectNoMatches',
  MeetingNotFound: 'trace.notFoundMeetingNotFound',
  UserDirectoryUnavailable: 'trace.notFoundUserDirectoryUnavailable',
};

function downloadJson(payload: unknown, filename: string) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(url), 150);
}

interface MatchesTableProps {
  matches: TraceMeetingMatch[];
  onSelect: (meetingId: string) => void;
}

function MatchesTable({ matches, onSelect }: MatchesTableProps) {
  const { t } = useTranslation();
  return (
    <section className="trc-matches">
      <h3 className={SECTION_TITLE_CLASS}>{t('trace.matchesTitle')}</h3>
      <div className="trc-table-wrap">
        <table className="trc-table">
          <thead>
            <tr>
              <th>{t('trace.matchesSubject')}</th>
              <th>{t('trace.matchesOrganizer')}</th>
              <th>{t('trace.matchesStatus')}</th>
              <th>{t('trace.matchesEnded')}</th>
            </tr>
          </thead>
          <tbody>
            {matches.map((match) => (
              <tr
                key={match.meetingId}
                className="trc-match-row"
                onClick={() => onSelect(match.meetingId)}
              >
                <td data-label={t('trace.matchesSubject')}>{match.subject}</td>
                <td data-label={t('trace.matchesOrganizer')}>{match.organizerId}</td>
                <td data-label={t('trace.matchesStatus')}>{match.status}</td>
                <td data-label={t('trace.matchesEnded')}>
                  {match.endedAt ? new Date(match.endedAt).toLocaleString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function MeetingSummary({ trace }: { trace: PipelineTrace }) {
  const { t } = useTranslation();
  return (
    <>
      <section className="trc-meeting-card">
        <h3 className={SECTION_TITLE_CLASS}>{trace.meeting.subject}</h3>
        <dl className="trc-meeting-facts">
          <div>
            <dt>{t('trace.meetingStatus')}</dt>
            <dd>{trace.meeting.status}</dd>
          </div>
          <div>
            <dt>{t('trace.meetingDuration')}</dt>
            <dd>{trace.meeting.durationMinutes?.toFixed(1) ?? '—'} min</dd>
          </div>
          <div>
            <dt>{t('trace.meetingCorrelation')}</dt>
            <dd>{trace.meeting.correlationId ?? '—'}</dd>
          </div>
        </dl>
        <button
          className="trc-btn-download"
          type="button"
          onClick={() => downloadJson(trace, `trace-${trace.meeting.id}.json`)}
        >
          <i className="fas fa-download" aria-hidden="true" /> {t('trace.download')}
        </button>
      </section>

      {trace.warnings.length > 0 && (
        <section className="trc-warnings">
          <h3 className={SECTION_TITLE_CLASS}>{t('trace.warningsTitle')}</h3>
          <ul>
            {trace.warnings.map((warning) => <li key={warning}>{warning}</li>)}
          </ul>
        </section>
      )}

      <TraceStagesTable trace={trace} />
      <TraceRawLogs entries={trace.rawLogs} />
    </>
  );
}

export function TrazabilidadTab() {
  const { t } = useTranslation();
  const { data, isPending, error, search, reset } = usePipelineTrace();
  // Últimos parámetros efectivamente enviados al backend (formulario o clic en una coincidencia),
  // para que "Reintentar" repita la búsqueda que realmente falló, sea cual sea su origen.
  const [lastParams, setLastParams] = useState<TraceQueryParams | null>(null);

  function runSearch(params: TraceQueryParams) {
    setLastParams(params);
    search(params);
  }

  function handleSearch(mode: TraceFilterMode, term: string) {
    runSearch(mode === 'email' ? { email: term } : { meetingSubject: term });
  }

  function handleSelectMatch(meetingId: string) {
    runSearch({ meetingId });
  }

  function handleRetry() {
    if (lastParams) runSearch(lastParams);
  }

  const trace = data?.trace ?? null;
  const matches = data?.matches ?? [];
  const notFoundReason = data?.notFoundReason ?? null;
  const hasNoResultYet = data === null && error === null;

  return (
    <div className="trc-tab">
      <header className="page-header">
        <div>
          <h2 className="page-title">
            <i className="fas fa-route" aria-hidden="true" /> {t('trace.title')}
          </h2>
          <p className="page-subtitle">{t('trace.subtitle')}</p>
        </div>
      </header>

      <TraceFilters isPending={isPending} onSearch={handleSearch} onClear={reset} />

      {hasNoResultYet && <p className="trc-initial-hint">{t('trace.initialHint')}</p>}

      {error && (
        <div className="trc-error" role="alert">
          <i className="fas fa-exclamation-triangle" aria-hidden="true" />
          <span>{t('trace.requestError')}</span>
          <button
            className="trc-btn-retry"
            type="button"
            onClick={handleRetry}
            disabled={isPending}
          >
            <i className={`fas fa-rotate-right${isPending ? ' fa-spin' : ''}`} aria-hidden="true" />
            {t('trace.retry')}
          </button>
        </div>
      )}

      {notFoundReason && (
        <div className="trc-empty">
          <i className="fas fa-inbox" aria-hidden="true" />
          <p>{t(NOT_FOUND_KEYS[notFoundReason])}</p>
        </div>
      )}

      {matches.length > 0 && <MatchesTable matches={matches} onSelect={handleSelectMatch} />}

      {trace && <MeetingSummary trace={trace} />}
    </div>
  );
}
